/**
 * The one place patient phone numbers are put into shape.
 *
 * Stored format is `+91XXXXXXXXXX`, matching the backend's `toE164India`.
 * Patients saved before that was enforced may still be stored as bare ten
 * digits, and Medplum's phone search is an exact match, so searches look for
 * both shapes.
 */

const TEN_DIGITS = /^\d{10}$/;

/** Digits only, without a `+91`/`91` country code or `0` trunk prefix. */
function nationalNumber(raw: string | null | undefined): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

/** `+91XXXXXXXXXX`, or undefined if the input isn't a ten-digit number. */
export function toE164India(raw: string | null | undefined): string | undefined {
  const national = nationalNumber(raw);
  return TEN_DIGITS.test(national) ? `+91${national}` : undefined;
}

/**
 * What a phone input should hold: at most ten digits. The country code is
 * dropped before the cap, so a pasted `+91 98765 43210` or a stored
 * `+919876543210` becomes 9876543210 instead of the wrong number 9198765432.
 */
export function sanitizePhoneInput(raw: string | null | undefined): string {
  return nationalNumber(raw).slice(0, 10);
}

/** Values a stored phone could take for this number — empty if it isn't a valid number. */
export function phoneSearchValues(raw: string | null | undefined): string[] {
  const e164 = toE164India(raw);
  return e164 ? [e164, e164.slice(3), `91${e164.slice(3)}`] : [];
}
